import { useState, useEffect, useRef, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNotification } from "@refinedev/core";
import { AxiosRequestConfig } from "axios";
import type { HttpError, MetaQuery, SuccessErrorNotification, MutationMode } from "@refinedev/core";
import { useAuth0Provider } from "@src/libs/auth0-provider";

export type UseSmartCustomMutationMethod = "POST" | "PUT" | "DELETE" | "post" | "put" | "delete";

// Extended variables interface that contains all possible config options
export interface UseSmartCustomMutationVariables {
    url: string; // Required when calling mutate
    method: UseSmartCustomMutationMethod; // Required when calling mutate
    values?: Record<string, any>;
    config?: {
        headers?: Record<string, any>;
        [key: string]: any;
    };
    dataProviderName?: string;

    // Refine specific options
    meta?: MetaQuery;
    metaData?: MetaQuery; // For backward compatibility
    mutationMode?: MutationMode;
    undoableTimeout?: number;
    onCancel?: (cancelMutation: () => void) => void;

    // Notification options
    successNotification?:
        | SuccessErrorNotification
        | false
        | ((data?: any, values?: any, resource?: string) => SuccessErrorNotification);
    errorNotification?:
        | SuccessErrorNotification
        | false
        | ((error?: any, values?: any, resource?: string) => SuccessErrorNotification);

    // Mutation options
    mutationOptions?: {
        retry?: number | boolean | ((failureCount: number, error: any) => boolean);
        onSuccess?: <TData = any>(data: TData, variables: UseSmartCustomMutationVariables, context: any) => void;
        onError?: (error: HttpError, variables: UseSmartCustomMutationVariables, context: any) => void;
        onSettled?: <TData = any>(
            data: TData | undefined,
            error: HttpError | null,
            variables: UseSmartCustomMutationVariables,
            context: any,
        ) => void;
        onMutate?: (variables: UseSmartCustomMutationVariables) => Promise<any> | any;
    };
}

export interface UseSmartCustomMutationResult<TData = any> {
    // Mutation properties
    data?: TData;
    statusCode?: number | null;
    error?: HttpError | null;
    isLoading: boolean;
    isError: boolean;
    isIdle: boolean;
    isSuccess: boolean;
    retryCount: number;

    // Mutation functions
    mutate: (variables: UseSmartCustomMutationVariables) => void;
    mutateAsync: (variables: UseSmartCustomMutationVariables) => Promise<TData>;
    debouncedMutateAsync: (variables: UseSmartCustomMutationVariables, timeout?: number) => Promise<TData>;

    // Control functions
    cancel: () => void;
    reset: () => void;
}

// Internal mutation variables interface
interface InternalMutationVariables extends UseSmartCustomMutationVariables {
    signal?: AbortSignal;
}

// Legacy config interface for backward compatibility (now optional)
export interface UseSmartCustomMutationConfig {
    url?: string;
    method?: UseSmartCustomMutationMethod;
    values?: Record<string, any>;
    config?: {
        headers?: Record<string, string>;
        [key: string]: any;
    };
    dataProviderName?: string;

    // Refine specific options
    meta?: MetaQuery;
    metaData?: MetaQuery; // For backward compatibility
    mutationMode?: MutationMode;
    undoableTimeout?: number;
    onCancel?: (cancelMutation: () => void) => void;

    // Notification options
    successNotification?:
        | SuccessErrorNotification
        | false
        | ((data?: any, values?: any, resource?: string) => SuccessErrorNotification);
    errorNotification?:
        | SuccessErrorNotification
        | false
        | ((error?: any, values?: any, resource?: string) => SuccessErrorNotification);

    // Mutation options
    mutationOptions?: {
        retry?: number | boolean | ((failureCount: number, error: any) => boolean);
        onSuccess?: <TData = any>(data: TData, variables: UseSmartCustomMutationVariables, context: any) => void;
        onError?: (error: HttpError, variables: UseSmartCustomMutationVariables, context: any) => void;
        onSettled?: <TData = any>(
            data: TData | undefined,
            error: HttpError | null,
            variables: UseSmartCustomMutationVariables,
            context: any,
        ) => void;
        onMutate?: (variables: UseSmartCustomMutationVariables) => Promise<any> | any;
    };
}

export const useSmartCustomMutation = <TData = any>(
    defaultConfig?: UseSmartCustomMutationConfig,
): UseSmartCustomMutationResult<TData> => {
    const { axios } = useAuth0Provider();
    const { open: openNotification } = useNotification();

    // State and refs for request management
    const abortControllerRef = useRef<AbortController | null>(null);
    const [isInternalLoading, setIsInternalLoading] = useState(false);
    const [statusCode, setStatusCode] = useState<number | null>(null);
    const [retryCount, setRetryCount] = useState(0);
    // Custom mutation function using axios with auto-retry for 202
    const customMutationFn = useCallback(
        async (variables: InternalMutationVariables): Promise<TData> => {
            const {
                url,
                method,
                values,
                config: requestConfig,
                meta: variableMeta,
                metaData: variableMetaData,
            } = variables;

            // Validate required fields
            if (!url) {
                throw new Error("URL is required for mutation");
            }

            if (!method) {
                throw new Error("Method is required for mutation");
            }

            // Merge meta and metaData (metaData for backward compatibility)
            const finalMeta = {
                ...variableMetaData,
                ...variableMeta,
                config: requestConfig,
            };

            const makeRequest = async (attemptCount = 0): Promise<TData> => {
                const signal = abortControllerRef.current?.signal;

                const axiosConfig: AxiosRequestConfig = {
                    url,
                    method: method.toLowerCase() as "post" | "put" | "delete" | "get",
                    data: values,
                    signal,
                    ...requestConfig,
                    headers: {
                        "Content-Type": "application/json",
                        ...finalMeta.config?.headers,
                    },
                };

                try {
                    const response = await axios(axiosConfig);
                    setStatusCode(response.status);

                    // If status is 202 (Accepted), retry after a delay
                    if (response.status === 202) {
                        const retryConfig = variables.mutationOptions?.retry;
                        const maxRetries = typeof retryConfig === "number" ? retryConfig : 10; // Default 10 retries
                        const retryDelay = Math.min(1000 * Math.pow(2, attemptCount), 10000); // Exponential backoff, max 10s

                        if (attemptCount < maxRetries) {
                            setRetryCount(attemptCount + 1);

                            // Wait before retrying
                            await new Promise((resolve) => setTimeout(resolve, retryDelay));

                            // Check if request was cancelled during delay
                            if (signal?.aborted) {
                                throw new Error("Request was cancelled");
                            }

                            return makeRequest(attemptCount + 1);
                        } else {
                            // Max retries reached, return the 202 response
                            return response.data;
                        }
                    }

                    // Reset retry count on successful response (non-202)
                    setRetryCount(0);
                    return response.data;
                } catch (error: any) {
                    setStatusCode(error.response?.status);
                    throw error;
                }
            };

            return makeRequest();
        },
        [axios],
    );

    // Setup the mutation with automatic cancellation
    const mutationResult = useMutation<TData, HttpError, InternalMutationVariables>({
        mutationFn: customMutationFn,
        onMutate: async (variables: InternalMutationVariables) => {
            setIsInternalLoading(true);

            // Cancel any ongoing requests automatically
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            // Create new AbortController for this request
            abortControllerRef.current = new AbortController();

            // Call user's onMutate callback from variables
            const context = await variables.mutationOptions?.onMutate?.(variables);
            return context;
        },

        onSuccess: (data, variables, context) => {
            setIsInternalLoading(false);

            // Handle success notification from variables
            if (variables.successNotification !== false && openNotification) {
                try {
                    if (typeof variables.successNotification === "function") {
                        const notification = variables.successNotification(data, variables, "custom");
                        // Only call if notification has the expected structure
                        if (notification && typeof notification === "object" && "message" in notification) {
                            openNotification(notification as any);
                        }
                    } else if (
                        variables.successNotification &&
                        typeof variables.successNotification === "object" &&
                        "message" in variables.successNotification
                    ) {
                        openNotification(variables.successNotification as any);
                    }
                } catch (notificationError) {
                    // Silently handle notification errors to not break the main flow
                    console.warn("Error displaying success notification:", notificationError);
                }
            }

            // Call user's onSuccess callback from variables
            variables.mutationOptions?.onSuccess?.(data, variables, context);
        },

        onError: (error, variables, context) => {
            setIsInternalLoading(false);

            // Handle error notification from variables
            if (variables.errorNotification !== false && openNotification) {
                try {
                    if (typeof variables.errorNotification === "function") {
                        const notification = variables.errorNotification(error, variables, "custom");
                        // Only call if notification has the expected structure
                        if (notification && typeof notification === "object" && "message" in notification) {
                            openNotification(notification as any);
                        }
                    } else if (
                        variables.errorNotification &&
                        typeof variables.errorNotification === "object" &&
                        "message" in variables.errorNotification
                    ) {
                        openNotification(variables.errorNotification as any);
                    }
                } catch (notificationError) {
                    // Silently handle notification errors to not break the main flow
                    console.warn("Error displaying error notification:", notificationError);
                }
            }

            // Call user's onError callback from variables
            variables.mutationOptions?.onError?.(error, variables, context);
        },

        onSettled: (data, error, variables, context) => {
            setIsInternalLoading(false);
            // Call user's onSettled callback from variables
            variables.mutationOptions?.onSettled?.(data, error, variables, context);
        },
    });

    // Enhanced mutate function that merges defaultConfig with variables
    const mutate = useCallback(
        (variables: UseSmartCustomMutationVariables) => {
            // Merge defaultConfig with variables (variables take precedence)
            const mergedVariables: InternalMutationVariables = {
                url: (variables.url || defaultConfig?.url) as string,
                method: (variables.method || defaultConfig?.method) as UseSmartCustomMutationMethod,
                values: variables.values || defaultConfig?.values,
                config: variables.config || defaultConfig?.config,
                dataProviderName: variables.dataProviderName || defaultConfig?.dataProviderName,
                meta: variables.meta || defaultConfig?.meta,
                metaData: variables.metaData || defaultConfig?.metaData,
                mutationMode: variables.mutationMode || defaultConfig?.mutationMode,
                undoableTimeout: variables.undoableTimeout || defaultConfig?.undoableTimeout,
                onCancel: variables.onCancel || defaultConfig?.onCancel,
                successNotification:
                    variables.successNotification !== undefined
                        ? variables.successNotification
                        : defaultConfig?.successNotification,
                errorNotification:
                    variables.errorNotification !== undefined
                        ? variables.errorNotification
                        : defaultConfig?.errorNotification,
                mutationOptions: {
                    ...defaultConfig?.mutationOptions,
                    ...variables.mutationOptions,
                },
            };

            // Validate required fields
            if (!mergedVariables.url) {
                throw new Error("URL is required for mutation. Please provide url in variables or defaultConfig.");
            }

            if (!mergedVariables.method) {
                throw new Error(
                    "Method is required for mutation. Please provide method in variables or defaultConfig.",
                );
            }

            mutationResult.mutate(mergedVariables);
        },
        [defaultConfig, mutationResult],
    );

    // Enhanced mutateAsync function that merges defaultConfig with variables
    const mutateAsync = useCallback(
        async (variables: UseSmartCustomMutationVariables): Promise<TData> => {
            // Merge defaultConfig with variables (variables take precedence)
            const mergedVariables: InternalMutationVariables = {
                url: (variables.url || defaultConfig?.url) as string,
                method: (variables.method || defaultConfig?.method) as UseSmartCustomMutationMethod,
                values: variables.values || defaultConfig?.values,
                config: variables.config || defaultConfig?.config,
                dataProviderName: variables.dataProviderName || defaultConfig?.dataProviderName,
                meta: variables.meta || defaultConfig?.meta,
                metaData: variables.metaData || defaultConfig?.metaData,
                mutationMode: variables.mutationMode || defaultConfig?.mutationMode,
                undoableTimeout: variables.undoableTimeout || defaultConfig?.undoableTimeout,
                onCancel: variables.onCancel || defaultConfig?.onCancel,
                successNotification:
                    variables.successNotification !== undefined
                        ? variables.successNotification
                        : defaultConfig?.successNotification,
                errorNotification:
                    variables.errorNotification !== undefined
                        ? variables.errorNotification
                        : defaultConfig?.errorNotification,
                mutationOptions: {
                    ...defaultConfig?.mutationOptions,
                    ...variables.mutationOptions,
                },
            };

            // Validate required fields
            if (!mergedVariables.url) {
                throw new Error("URL is required for mutation. Please provide url in variables or defaultConfig.");
            }

            if (!mergedVariables.method) {
                throw new Error(
                    "Method is required for mutation. Please provide method in variables or defaultConfig.",
                );
            }

            return mutationResult.mutateAsync(mergedVariables);
        },
        [defaultConfig, mutationResult],
    );

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const debouncedMutateAsync = useCallback(
        (variables: UseSmartCustomMutationVariables, timeout = 100): Promise<TData> => {
            return new Promise((resolve, reject) => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                timeoutRef.current = setTimeout(() => {
                    mutateAsync(variables).then(resolve).catch(reject);
                }, timeout);
            });
        },
        [mutateAsync],
    );

    // Cancel function to abort current requests
    const cancel = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setIsInternalLoading(false);
        setRetryCount(0);
        mutationResult.reset();
    }, [mutationResult]);

    // Reset function
    const reset = useCallback(() => {
        cancel();
        setRetryCount(0);
        mutationResult.reset();
    }, [cancel, mutationResult]);

    // Clean up abort controller on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Set up onCancel callback if provided in defaultConfig
    useEffect(() => {
        if (defaultConfig?.onCancel) {
            defaultConfig.onCancel(cancel);
        }
    }, [defaultConfig, cancel]);

    return {
        data: mutationResult.data,
        statusCode,
        error: mutationResult.error,
        isLoading: mutationResult.isLoading || isInternalLoading,
        isError: mutationResult.isError,
        isIdle: mutationResult.isIdle,
        isSuccess: mutationResult.isSuccess,
        retryCount,
        mutate,
        mutateAsync,
        debouncedMutateAsync,
        cancel,
        reset,
    };
};
